import { Adb, AdbDaemonTransport } from '@yume-chan/adb';
import { AdbDaemonWebUsbDeviceManager, AdbDaemonWebUsbDevice } from '@yume-chan/adb-daemon-webusb';
import AdbWebCredentialStore from '@yume-chan/adb-credential-web';
import { FileEntry } from '../stores/files.store';

export type AdbLogLevel = 'info' | 'success' | 'error' | 'warning';
type LogCallback = (message: string, level?: AdbLogLevel) => void;

export class WebAdbService {
  private static instance: WebAdbService;
  private credentialStore: AdbWebCredentialStore;
  private manager: AdbDaemonWebUsbDeviceManager | null = null;
  private logCallback?: LogCallback;

  private constructor() {
    this.credentialStore = new AdbWebCredentialStore();
    try {
      this.manager = AdbDaemonWebUsbDeviceManager.BROWSER ?? null;
      if (!this.manager) {
        console.warn('AdbDaemonWebUsbDeviceManager.BROWSER is not available');
      }
    } catch (e) {
      console.error('Failed to initialize AdbDaemonWebUsbDeviceManager', e);
    }
  }

  public static getInstance(): WebAdbService {
    if (!WebAdbService.instance) {
      WebAdbService.instance = new WebAdbService();
    }
    return WebAdbService.instance;
  }

  public setLogCallback(callback: LogCallback) {
    this.logCallback = callback;
  }

  private log(message: string, level: AdbLogLevel = 'info') {
    if (this.logCallback) {
      this.logCallback(message, level);
    } else {
      console.log(`[${level.toUpperCase()}] ${message}`);
    }
  }

  async getPairedDevices(): Promise<AdbDaemonWebUsbDevice[]> {
    if (!this.manager) return [];
    try {
      return await this.manager.getDevices();
    } catch (e) {
      this.log('获取已配对设备失败', 'error');
      return [];
    }
  }

  async requestDevice(): Promise<AdbDaemonWebUsbDevice | undefined> {
    if (!this.manager) {
      throw new Error('您的浏览器不支持 WebUSB，请使用 Chrome 或 Edge 浏览器。');
    }
    try {
      return await this.manager.requestDevice();
    } catch (e: any) {
      if (e.name === 'NotFoundError') {
        this.log('未选择设备', 'warning');
        return undefined;
      }
      throw e;
    }
  }

  async connect(device: AdbDaemonWebUsbDevice): Promise<Adb> {
    this.log(`正在连接设备: ${device.serial}...`, 'info');
    try {
      const connection = await device.connect();
      const transport = await AdbDaemonTransport.authenticate({
        serial: device.serial,
        connection,
        credentialStore: this.credentialStore,
      });
      const adb = new Adb(transport);
      this.log(`设备 ${device.serial} 连接成功`, 'success');
      return adb;
    } catch (error: any) {
      this.log(`连接设备失败: ${error.message}`, 'error');
      throw error;
    }
  }

  async executeShell(adb: Adb, command: string, timeout = 30000): Promise<string> {
    // 创建超时 Promise
    let timeoutId: ReturnType<typeof setTimeout>;
    const timeoutPromise = new Promise<string>((_, reject) => {
      timeoutId = setTimeout(() => {
        reject(new Error(`命令执行超时: ${command}`));
      }, timeout);
    });

    let outputPromise: Promise<string>;
    
    if (adb.subprocess.shellProtocol) {
      outputPromise = adb.subprocess.shellProtocol.spawnWaitText(command).then(r => r.stdout);
    } else {
      outputPromise = adb.subprocess.noneProtocol.spawnWaitText(command);
    }

    try {
      const output = await Promise.race([outputPromise, timeoutPromise]);
      return output.trim();
    } catch (error: any) {
      this.log(`执行命令失败: ${command}, 错误: ${error.message}`, 'error');
      throw error;
    } finally {
      clearTimeout(timeoutId!);
    }
  }

  async findStoragePath(adb: Adb): Promise<string> {
    const pathsToCheck = ['/sdcard', '/mnt/sdcard', '/storage/emulated/0', '/storage/self/primary'];
    for (const path of pathsToCheck) {
      try {
        // 使用 ls -ld 检查路径，并跟随符号链接
        const result = await this.executeShell(adb, `ls -ld "${path}" 2>&1`);
        if (result && !result.includes('No such file') && !result.includes('Permission denied')) {
          // 检查是否是符号链接
          const isLink = result.trim().startsWith('l');
          
          // 如果是符号链接，获取实际路径
          let actualPath = path;
          if (isLink) {
            // 解析符号链接 - 使用 realpath 比 readlink -f 更可靠
            let linkTarget = await this.executeShell(adb, `realpath "${path}"`);
            if (!linkTarget || linkTarget.includes('No such file') || linkTarget.includes('command not found')) {
              // 如果 realpath 不可用，回退到 readlink -f
              linkTarget = await this.executeShell(adb, `readlink -f "${path}"`);
            }
            if (linkTarget && !linkTarget.includes('No such file')) {
              actualPath = linkTarget.trim();
              // 确保路径以 / 开头（处理某些手机返回相对路径的情况）
              if (!actualPath.startsWith('/')) {
                actualPath = '/' + actualPath;
              }
              this.log(`${path} 是符号链接，实际路径: ${actualPath}`, 'info');
            }
          }
          
          // 测试能否列出目录内容
          const testLs = await this.executeShell(adb, `ls "${actualPath}" 2>&1 | head -n 1`);
          if (!testLs.includes('Permission denied') && testLs.trim() !== '') {
            return actualPath.endsWith('/') ? actualPath : actualPath + '/';
          }
        }
      } catch (e) {
        // ignore
      }
    }
    throw new Error('未找到可用存储路径');
  }

  // 排序：目录优先，同类型按文件名自然排序（不区分大小写）
  private sortFileEntries(entries: FileEntry[]): FileEntry[] {
    return [...entries].sort((a, b) => {
      if (a.type !== b.type) {
        return a.type === 'directory' ? -1 : 1;
      }
      return a.name.localeCompare(b.name, undefined, { numeric: true, sensitivity: 'base' });
    });
  }

  // 去重：如果文件在某个匹配的目录下，则移除该文件
  private deduplicateSearchResults(entries: FileEntry[]): FileEntry[] {
    const directories = entries.filter(e => e.type === 'directory').map(e => e.path);

    // 如果没有目录，直接返回
    if (directories.length === 0) {
      return entries;
    }

    // 过滤掉在匹配目录下的文件
    return entries.filter(entry => {
      // 保留所有目录
      if (entry.type === 'directory') {
        return true;
      }

      // 检查文件是否在某个匹配的目录下
      const isInMatchedDir = directories.some(dir => {
        // 文件路径必须以 "目录路径/" 开头才算在目录下
        return entry.path.startsWith(dir + '/');
      });

      // 只保留不在匹配目录下的文件
      return !isInMatchedDir;
    });
  }

  async findFiles(adb: Adb, path: string, rules: string[], recursive = false): Promise<FileEntry[]> {
    this.log(`正在查找: ${path}, 规则: ${rules.join(', ')}, 递归: ${recursive}`, 'info');

    const searchPath = (path.endsWith('/') && path !== '/') ? path.slice(0, -1) : path;

    // 对于简单的 '*' 规则，直接使用 ls 命令更可靠
    if (rules.length === 1 && rules[0] === '*') {
      return this.listDirectory(adb, searchPath);
    }

    // 递归搜索时使用更长的超时时间（5分钟）
    const timeout = recursive ? 300000 : 30000;

    try {
      // 方法1: 使用 ls + grep（最兼容，适合简单搜索）
      const results = await this.findFilesWithLsGrep(adb, searchPath, rules, recursive, timeout);
      if (results.length > 0) {
        return results;
      }

      // 方法2: 使用 find 命令（标准方式，但某些设备可能不支持）
      this.log('ls + grep 无结果，尝试使用 find 命令', 'info');
      return await this.findFilesWithFind(adb, searchPath, rules, recursive, timeout);
    } catch (error: any) {
      this.log(`查找失败: ${error.message}`, 'warning');
      return [];
    }
  }

  private async findFilesWithLsGrep(adb: Adb, searchPath: string, rules: string[], recursive: boolean, timeout: number): Promise<FileEntry[]> {
    // 增加结果限制到 500 条
    const MAX_RESULTS = 500;

    // 直接使用 find 命令的 -name 选项，这是最可靠的方式
    const nameArgs = rules.map(r => `-name '${r}'`).join(' -o ');
    this.log(`搜索规则: ${nameArgs}`, 'info');

    // 分别搜索文件和目录
    const findFilesCommand = `find "${searchPath}" -type f \\( ${nameArgs} \\) 2>/dev/null | head -${MAX_RESULTS}`;
    const findDirsCommand = `find "${searchPath}" -type d \\( ${nameArgs} \\) 2>/dev/null | head -${MAX_RESULTS}`;

    this.log(`执行命令: ${findFilesCommand}`, 'info');
    this.log(`执行命令: ${findDirsCommand}`, 'info');

    const [filesOutput, dirsOutput] = await Promise.all([
      this.executeShell(adb, findFilesCommand, timeout).catch(() => ''),
      this.executeShell(adb, findDirsCommand, timeout).catch(() => '')
    ]);

    let output = filesOutput + '\n' + dirsOutput;

    // 如果 find -name 无结果，尝试使用 find + grep 的组合方式
    // 这对于复杂的通配符模式（如 *2026_03_01_*-location.csv）更兼容
    if (!output || output.trim() === '') {
      this.log('find -name 无结果，尝试使用 find + grep 组合', 'info');

      // 将通配符转换为 grep -E 正则表达式
      // grep -E 多模式用 | 分隔（不加反斜杠）
      const grepPatterns = rules.map(rule => {
        return rule
          .replace(/[.+^${}()[\]\\]/g, '\\$&')  // 转义特殊字符（保留 | 不转义）
          .replace(/\*/g, '.*')                   // * 转为 .*
          .replace(/\?/g, '.');                   // ? 转为 .
      });
      // 用 | 拼接，匹配路径末尾的文件名（/之后到行尾）
      const grepPattern = grepPatterns.join('|');
      const grepCommand = `find "${searchPath}" 2>/dev/null | grep -E '/(${grepPattern})$' | head -${MAX_RESULTS}`;

      this.log(`执行 grep 命令: ${grepCommand}`, 'info');
      output = await this.executeShell(adb, grepCommand, timeout);
    }

    if (!output || output.trim() === '') {
      this.log('所有搜索方式均无结果', 'warning');
      return [];
    }

    const lines = output.split('\n').filter(Boolean);

    // 创建一个 Set 来存储目录路径，用于快速判断
    const dirPaths = new Set(dirsOutput.split('\n').filter(Boolean).map(p => p.trim()));

    const results: FileEntry[] = [];

    for (const line of lines) {
      const cleanPath = line.trim();
      if (!cleanPath || cleanPath === searchPath) continue;

      // 如果是非递归搜索，只保留直接子项
      if (!recursive) {
        const relativePath = cleanPath.substring(searchPath.length).replace(/^\//, '');
        if (relativePath.includes('/')) continue;
      }

      const name = cleanPath.split('/').pop() || cleanPath;
      const isDirectory = dirPaths.has(cleanPath);

      results.push({
        name,
        path: cleanPath,
        type: isDirectory ? 'directory' : 'file',
        size: 0,
        modifiedTime: new Date(),
        provider: 'adb',
      });

      // 限制结果数量
      if (results.length >= MAX_RESULTS) break;
    }

    // 去重：如果文件在某个匹配的目录下，则移除该文件
    const filteredResults = this.deduplicateSearchResults(results);

    if (filteredResults.length > 0) {
      this.log(`查找到 ${filteredResults.length} 个项目`, 'success');
    }

    // 按文件名排序
    return this.sortFileEntries(filteredResults);
  }

  private async findFilesWithFind(adb: Adb, searchPath: string, rules: string[], recursive: boolean, timeout: number): Promise<FileEntry[]> {
    // 增加结果限制到 500 条
    const MAX_RESULTS = 500;

    const nameArgs = rules.map(r => `-name '${r}'`).join(' -o ');
    this.log(`find 命令搜索规则: ${nameArgs}`, 'info');

    // 不使用 -maxdepth，因为某些设备不支持
    // 对于非递归搜索，我们在结果中过滤
    const findFilesCommand = `find "${searchPath}" -type f \\( ${nameArgs} \\) 2>/dev/null | head -${MAX_RESULTS}`;
    const findDirsCommand = `find "${searchPath}" \\( -type d -o -type l \\) \\( ${nameArgs} \\) 2>/dev/null | head -${MAX_RESULTS}`;

    this.log(`执行 find 命令: ${findFilesCommand}`, 'info');

    const [filesOutput, dirsOutput] = await Promise.all([
      this.executeShell(adb, findFilesCommand, timeout).catch(() => ''),
      this.executeShell(adb, findDirsCommand, timeout).catch(() => '')
    ]);

    const filePaths = filesOutput.split('\n').filter(Boolean);
    const dirPaths = dirsOutput.split('\n').filter(Boolean);

    if (filePaths.length === 0 && dirPaths.length === 0) {
      this.log('find 命令未找到匹配项', 'warning');
      return [];
    }

    this.log(`find 找到 ${filePaths.length + dirPaths.length} 个匹配项（${filePaths.length} 个文件，${dirPaths.length} 个目录）`, 'info');

    const results: FileEntry[] = [];

    // 如果是非递归搜索，只保留直接子项
    const filterNonRecursive = (path: string): boolean => {
      if (recursive) return true;
      const relativePath = path.substring(searchPath.length).replace(/^\//, '');
      return !relativePath.includes('/');
    };

    for (const filePath of filePaths.slice(0, MAX_RESULTS)) {
      const cleanPath = filePath.trim();
      if (!cleanPath || cleanPath === searchPath) continue;
      if (!filterNonRecursive(cleanPath)) continue;

      const name = cleanPath.split('/').pop() || cleanPath;

      results.push({
        name,
        path: cleanPath,
        type: 'file',
        size: 0,
        modifiedTime: new Date(),
        provider: 'adb',
      });
    }

    for (const dirPath of dirPaths.slice(0, MAX_RESULTS - results.length)) {
      const cleanPath = dirPath.trim();
      if (!cleanPath || cleanPath === searchPath) continue;
      if (!filterNonRecursive(cleanPath)) continue;

      const name = cleanPath.split('/').pop() || cleanPath;

      results.push({
        name,
        path: cleanPath,
        type: 'directory',
        size: 0,
        modifiedTime: new Date(),
        provider: 'adb',
      });
    }

    // 去重：如果文件在某个匹配的目录下，则移除该文件
    const filteredResults = this.deduplicateSearchResults(results);

    if (filteredResults.length > 0) {
      this.log(`查找到 ${filteredResults.length} 个项目 (find 命令)`, 'success');
    }

    return this.sortFileEntries(filteredResults);
  }

  private async listDirectory(adb: Adb, path: string): Promise<FileEntry[]> {
    // 方法1: 使用 ls -la 获取详细信息
    const output = await this.executeShell(adb, `ls -la "${path}" 2>/dev/null`);

    // 如果输出为空，尝试备用方法
    if (!output || output.trim() === '') {
      return this.listDirectoryFallback(adb, path);
    }

    const lines = output.split('\n').filter(Boolean);

    const results = this.parseLsOutput(path, lines);

    if (results.length === 0) {
      // 如果解析失败，尝试备用方法
      return this.listDirectoryFallback(adb, path);
    }

    this.log(`解析到 ${lines.length} 行`, 'info');
    this.log(`列出 ${results.length} 个项目`, 'success');
    return this.sortFileEntries(results);
  }

  private parseLsOutput(path: string, lines: string[]): FileEntry[] {
    const results: FileEntry[] = [];

    for (const line of lines) {
      // 跳过 total 行
      if (line.startsWith('total')) continue;

      // ls -la 格式: drwxrwxrwx 2 user group size date time name
      // Android 格式可能是: drwxrwx--x 3 root sdcard_rw 4096 2024-01-01 12:00 name
      const parts = line.trim().split(/\s+/);

      // 跳过权限为 ????????? 的行（无权限访问）
      if (parts[0].includes('?')) continue;

      if (parts.length < 8) continue;

      const permissions = parts[0];
      const isDir = permissions.startsWith('d');
      const isLink = permissions.startsWith('l');
      const size = parseInt(parts[4]) || 0;

      // 文件名从第8个字段开始（索引7），可能包含空格
      // 找到第8个字段的起始位置
      let idx = 0;
      for (let i = 0; i < 7 && idx < line.length; i++) {
        // 跳过当前字段
        while (idx < line.length && !/\s/.test(line[idx])) idx++;
        // 跳过空白
        while (idx < line.length && /\s/.test(line[idx])) idx++;
      }

      let name = line.substring(idx).trim();

      // 处理符号链接 (name -> target)
      if (isLink && name.includes(' -> ')) {
        name = name.split(' -> ')[0];
      }

      if (!name || name === '.' || name === '..') continue;

      const fullPath = path.endsWith('/') ? path + name : path + '/' + name;

      results.push({
        name,
        path: fullPath,
        type: isDir || isLink ? 'directory' : 'file',
        size: isDir ? 0 : size,
        modifiedTime: new Date(),
        provider: 'adb',
      });
    }

    return results;
  }

  private async listDirectoryFallback(adb: Adb, path: string): Promise<FileEntry[]> {
    this.log(`尝试备用方法列出目录: ${path}`, 'info');
    const results: FileEntry[] = [];

    try {
      // 方法2: 使用 ls -1a（简单列出，不包含详细信息）
      const simpleOutput = await this.executeShell(adb, `ls -1a "${path}" 2>/dev/null`);

      if (simpleOutput && simpleOutput.trim() !== '') {
        const names = simpleOutput.split('\n').filter(Boolean);

        for (const name of names) {
          if (!name || name === '.' || name === '..') continue;

          const fullPath = path.endsWith('/') ? path + name : path + '/' + name;

          // 检查是目录还是文件
          let isDir = false;
          try {
            const testOutput = await this.executeShell(adb, `test -d "${fullPath}" && echo "dir" || echo "file"`, 5000);
            isDir = testOutput.trim() === 'dir';
          } catch {
            // 如果检查失败，尝试通过其他方式判断
            try {
              const lsOutput = await this.executeShell(adb, `ls -ld "${fullPath}" 2>/dev/null | head -1`);
              isDir = lsOutput.startsWith('d');
            } catch {
              // 默认当作文件
            }
          }

          results.push({
            name,
            path: fullPath,
            type: isDir ? 'directory' : 'file',
            size: 0,
            modifiedTime: new Date(),
            provider: 'adb',
          });
        }

        if (results.length > 0) {
          this.log(`备用方法列出 ${results.length} 个项目`, 'success');
        }
        return this.sortFileEntries(results);
      }

      // 方法3: 对于 /storage/emulated 这样的特殊目录，尝试直接列出已知的子目录
      if (path === '/storage/emulated' || path === '/storage/emulated/') {
        this.log('尝试列出 emulated 目录的已知子项', 'info');

        // 检查常见的用户目录 (0, 1, 2, ...)
        for (let i = 0; i <= 10; i++) {
          const testPath = path.endsWith('/') ? path + i : path + '/' + i;
          try {
            const testOutput = await this.executeShell(adb, `test -d "${testPath}" && echo "exists"`, 3000);
            if (testOutput.includes('exists')) {
              results.push({
                name: String(i),
                path: testPath,
                type: 'directory',
                size: 0,
                modifiedTime: new Date(),
                provider: 'adb',
              });
            }
          } catch {
            // 忽略
          }
        }

        if (results.length > 0) {
          this.log(`找到 ${results.length} 个 emulated 子目录`, 'success');
          return this.sortFileEntries(results);
        }
      }

      this.log(`目录为空或无法访问: ${path}`, 'warning');
    } catch (error: any) {
      this.log(`备用方法失败: ${error.message}`, 'warning');
    }

    return results;
  }

  async getDeviceInfo(adb: Adb): Promise<{ model: string; androidVersion: string; storage: { used: string; total: string; percent: number }; battery: number; isRooted: boolean }> {
    const model = (await this.executeShell(adb, 'getprop ro.product.model')).trim();
    const androidVersion = (await this.executeShell(adb, 'getprop ro.build.version.release')).trim();

    // 解析存储信息
    let storage = { used: '0', total: '0', percent: 0 };
    try {
      const df = await this.executeShell(adb, 'df /sdcard 2>/dev/null | tail -1');
      const parts = df.trim().split(/\s+/);
      if (parts.length >= 4) {
        const totalKB = parseInt(parts[1]) || 0;
        const usedKB = parseInt(parts[2]) || 0;
        storage = {
          total: (totalKB / 1024 / 1024).toFixed(1) + ' GB',
          used: (usedKB / 1024 / 1024).toFixed(1) + ' GB',
          percent: totalKB > 0 ? Math.round((usedKB / totalKB) * 100) : 0
        };
      }
    } catch { /* ignore */ }

    // 获取电量
    let battery = 0;
    try {
      const batteryOutput = await this.executeShell(adb, 'cat /sys/class/power_supply/battery/capacity 2>/dev/null || dumpsys battery | grep level | cut -d: -f2');
      battery = parseInt(batteryOutput.trim()) || 0;
    } catch { /* ignore */ }

    // 检查 root 状态
    let isRooted = false;
    try {
      // 检查当前 shell 是否为 root (uid=0)
      const idOutput = await this.executeShell(adb, 'id -u');
      if (idOutput.trim() === '0') {
        isRooted = true;
      } else {
        // 检查常见的 su 路径和 Magisk
        const rootCheck = await this.executeShell(adb, 'ls /system/xbin/su /system/bin/su /sbin/su /data/adb/magisk 2>/dev/null | head -1');
        isRooted = rootCheck.trim().length > 0;
      }
    } catch { /* ignore */ }

    return { model, androidVersion, storage, battery, isRooted };
  }

  async runFullDiagnostic(adb: Adb) {
    this.log('=== 开始完整诊断 ===', 'info');
    try {
      this.log('--- 设备信息 ---', 'info');
      const model = await this.executeShell(adb, 'getprop ro.product.model');
      this.log(`型号: ${model.trim()}`);
      const androidVersion = await this.executeShell(adb, 'getprop ro.build.version.release');
      this.log(`Android 版本: ${androidVersion.trim()}`);
      
      this.log('--- 存储信息 ---', 'info');
      const df = await this.executeShell(adb, 'df -h /sdcard 2>/dev/null || df /sdcard');
      this.log(`存储空间:\n${df}`);
      
      this.log('--- 权限检查 ---', 'info');
      const lsTest = await this.executeShell(adb, 'ls -d /sdcard/ 2>&1');
      if (lsTest.includes('Permission denied')) {
        this.log('⚠️ /sdcard 访问被拒绝', 'warning');
      } else {
        this.log('✓ /sdcard 可访问', 'success');
      }
      
      this.log('=== 诊断完成 ===', 'success');
    } catch (error: any) {
      this.log(`诊断失败: ${error.message}`, 'error');
    }
  }

  /**
   * 使用 ADB sync 协议直接下载单个文件到本地（高速）
   */
  async downloadFileToLocal(
    adb: Adb,
    adbPath: string,
    targetHandle: FileSystemDirectoryHandle,
    fileName: string,
    onProgress?: (bytes: number) => void
  ): Promise<number> {
    const sync = await adb.sync();
    let totalBytes = 0;

    try {
      const fileHandle = await targetHandle.getFileHandle(fileName, { create: true });
      const writable = await fileHandle.createWritable();
      const stream = await sync.read(adbPath);

      if (onProgress) {
        let lastReport = 0;
        const countingStream = new TransformStream<Uint8Array, Uint8Array>({
          transform(chunk, controller) {
            totalBytes += chunk.length;
            if (totalBytes - lastReport >= 5 * 1024 * 1024) {
              lastReport = totalBytes;
              onProgress(totalBytes);
            }
            controller.enqueue(chunk);
          }
        });
        await (stream as ReadableStream<Uint8Array>).pipeThrough(countingStream).pipeTo(writable);
        onProgress(totalBytes);
      } else {
        await (stream as ReadableStream<Uint8Array>).pipeTo(writable);
        const file = await fileHandle.getFile();
        totalBytes = file.size;
      }
    } finally {
      sync.dispose();
    }

    return totalBytes;
  }

  /**
   * 下载目录到本地（递归，并行）
   */
  async downloadDirectoryToLocal(
    adb: Adb,
    adbPath: string,
    targetHandle: FileSystemDirectoryHandle,
    dirName: string
  ): Promise<void> {
    const newDirHandle = await targetHandle.getDirectoryHandle(dirName, { create: true });
    const entries = await this.listDirectory(adb, adbPath);

    // 并行处理
    const CONCURRENCY = 4;
    const active = new Set<Promise<void>>();

    for (const entry of entries) {
      const task = (async () => {
        if (entry.type === 'file') {
          await this.downloadFileToLocal(adb, entry.path, newDirHandle, entry.name);
        } else {
          await this.downloadDirectoryToLocal(adb, entry.path, newDirHandle, entry.name);
        }
      })().finally(() => active.delete(task));

      active.add(task);
      if (active.size >= CONCURRENCY) {
        await Promise.race(active);
      }
    }
    await Promise.all(active);
  }

  /**
   * 并行下载多个文件到本地目录（高速模式）
   */
  async downloadFilesParallel(
    adb: Adb,
    items: FileEntry[],
    targetHandle: FileSystemDirectoryHandle,
    onProgress?: (current: number, total: number, bytes: number) => void
  ): Promise<number> {
    const files = items.filter(i => i.type === 'file');
    const dirs = items.filter(i => i.type === 'directory');
    
    // 先递归获取目录中的所有文件
    const allFiles: { adbPath: string; relativePath: string }[] = [];
    
    for (const file of files) {
      allFiles.push({ adbPath: file.path, relativePath: file.name });
    }
    
    for (const dir of dirs) {
      const dirFiles = await this.listAllFilesRecursive(adb, dir.path, dir.name);
      allFiles.push(...dirFiles);
    }
    
    this.log(`准备下载 ${allFiles.length} 个文件`, 'info');
    
    let totalBytes = 0;
    let completed = 0;
    let lastReport = 0;
    const total = allFiles.length;

    // 并发下载，限制为 3 个同时进行
    const CONCURRENCY = 3;
    const queue = [...allFiles];
    const active = new Set<Promise<void>>();

    const processFile = async (file: { adbPath: string; relativePath: string }, retries = 2) => {
      const sync = await adb.sync();
      try {
        // 创建目录结构
        const parts = file.relativePath.split('/');
        const fileName = parts.pop()!;
        let currentDir = targetHandle;

        // 清理文件名中的非法字符 (Windows: \ / : * ? " < > |)
        const sanitize = (name: string) => name.replace(/[\\/:*?"<>|]/g, '_');

        for (const part of parts) {
          currentDir = await currentDir.getDirectoryHandle(sanitize(part), { create: true });
        }

        // 下载文件 - 使用 pipeTo 提高速度
        const fileHandle = await currentDir.getFileHandle(sanitize(fileName), { create: true });
        const writable = await fileHandle.createWritable();
        const stream = await sync.read(file.adbPath);

        const countingStream = new TransformStream<Uint8Array, Uint8Array>({
          transform(chunk, controller) {
            totalBytes += chunk.length;
            if (totalBytes - lastReport >= 5 * 1024 * 1024) {
              lastReport = totalBytes;
              onProgress?.(completed, total, totalBytes);
            }
            controller.enqueue(chunk);
          }
        });
        await (stream as ReadableStream<Uint8Array>).pipeThrough(countingStream).pipeTo(writable);

        completed++;
        onProgress?.(completed, total, totalBytes);
      } catch (e: any) {
        sync.dispose();
        if (retries > 0) {
          await new Promise(r => setTimeout(r, 100));
          return processFile(file, retries - 1);
        }
        throw e;
      } finally {
        sync.dispose();
      }
    };
    
    while (queue.length > 0 || active.size > 0) {
      while (active.size < CONCURRENCY && queue.length > 0) {
        const file = queue.shift()!;
        const promise = processFile(file).catch(e => {
          this.log(`下载失败 ${file.relativePath}: ${e.message}`, 'error');
          completed++;
        }).finally(() => active.delete(promise));
        active.add(promise);
      }
      
      if (active.size > 0) {
        await Promise.race(active);
      }
    }
    
    this.log(`下载完成，共 ${(totalBytes / 1024 / 1024).toFixed(2)} MB`, 'success');
    return totalBytes;
  }

  private async listAllFilesRecursive(adb: Adb, basePath: string, relativePath: string): Promise<{ adbPath: string; relativePath: string }[]> {
    const result: { adbPath: string; relativePath: string }[] = [];
    const entries = await this.listDirectory(adb, basePath);
    
    for (const entry of entries) {
      const entryRelPath = relativePath + '/' + entry.name;
      if (entry.type === 'file') {
        result.push({ adbPath: entry.path, relativePath: entryRelPath });
      } else {
        const subFiles = await this.listAllFilesRecursive(adb, entry.path, entryRelPath);
        result.push(...subFiles);
      }
    }
    
    return result;
  }

  /**
   * 使用 tar 流式打包提取（备选方案，适合大量小文件）
   */
  async extractToTarStream(adb: Adb, items: FileEntry[]): Promise<{ stream: ReadableStream<Uint8Array>, filename: string }> {
    if (items.length === 0) throw new Error('没有选择任何项目');

    // Find common parent path
    const firstPath = items[0].path;
    const lastSlashIndex = firstPath.lastIndexOf('/');
    const basePath = lastSlashIndex <= 0 ? '/' : firstPath.substring(0, lastSlashIndex);
    
    const timestamp = new Date().toISOString().replace(/[:T]/g, '-').slice(0, 19);
    const filename = `adb_extract_${timestamp}.tar`;

    this.log(`正在通过流式打包提取 ${items.length} 个项目...`, 'info');
    
    // Escape paths for shell
    const escapedPaths = items.map(item => {
      const relativePath = item.path.startsWith(basePath)
        ? item.path.substring(basePath.length).replace(/^\//, '')
        : item.path;
      return `"${relativePath}"`;
    }).join(' ');

    try {
      const spawner = adb.subprocess.shellProtocol ?? adb.subprocess.noneProtocol;
      if (!spawner) {
        throw new Error('设备不支持 Subprocess 协议，无法执行流式打包');
      }
      
      // 使用 --blocking-factor 优化大文件传输
      const process = await spawner.spawn(`cd "${basePath}" && tar --blocking-factor=20 -cf - ${escapedPaths}`);
      const stdout = 'stdout' in process ? (process as any).stdout : (process as any).output;
      
      return {
        stream: stdout as any as ReadableStream<Uint8Array>,
        filename
      };
    } catch (error: any) {
      this.log(`流式提取失败: ${error.message}`, 'error');
      throw error;
    }
  }

  async downloadFile(adb: Adb, adbPath: string): Promise<ReadableStream<Uint8Array>> {
    const sync = await adb.sync();
    const stream = await sync.read(adbPath);
    
    // 使用 TransformStream 包装以确保在流结束或取消时释放 sync 资源
    // 这种方式天然支持背压，因为 ReadableStream 的读取速度会限制底层 ADB 传输
    const { readable, writable } = new TransformStream<Uint8Array, Uint8Array>();
    
    (stream as any).pipeTo(writable).finally(() => {
      sync.dispose();
    });

    return readable;
  }

  async rename(adb: Adb, oldPath: string, newPath: string): Promise<void> {
    this.log(`重命名: ${oldPath} -> ${newPath}`, 'info');
    await this.executeShell(adb, `mv "${oldPath}" "${newPath}"`);
  }

  /**
   * 导出 Logcat 日志
   */
  async exportLogcat(adb: Adb): Promise<string> {
    this.log('正在导出 Logcat 日志...', 'info');
    const output = await this.executeShell(adb, 'logcat -d', 60000);
    this.log(`Logcat 导出完成，共 ${output.length} 字符`, 'success');
    return output;
  }

  /**
   * 清除 Logcat 日志
   */
  async clearLogcat(adb: Adb): Promise<void> {
    this.log('正在清除 Logcat 日志...', 'info');
    await this.executeShell(adb, 'logcat -c');
    this.log('Logcat 日志已清除', 'success');
  }

  /**
   * 生成 Bugreport (流式方式，支持长时间生成)
   * @param onProgress 进度回调 (status: 当前状态, percent: 百分比 0-100)
   */
  async generateBugreport(adb: Adb, onProgress?: (status: string, percent: number) => void): Promise<string> {
    this.log('正在生成 Bugreport，这可能需要几分钟...', 'info');

    // 检查设备是否支持 bugreport
    try {
      const check = await this.executeShell(adb, 'which bugreport 2>&1', 5000);
      if (check.includes('not found') || check.includes('command not found')) {
        throw new Error('设备不支持 bugreport 命令');
      }
    } catch (e) {
      // 忽略检查错误，继续尝试
    }

    let output = '';
    let hasOutput = false;
    let startTime = Date.now();
    const maxDuration = 10 * 60 * 1000; // 10 分钟最大时长

    // Bugreport 进度阶段（基于常见输出模式）
    const progressStages = [
      { pattern: /=== build\.prop ===/, name: '读取系统属性', percent: 5 },
      { pattern: /=== check_service ===/, name: '检查系统服务', percent: 10 },
      { pattern: /=== dumpstate ===/, name: '收集设备状态', percent: 20 },
      { pattern: /=== dumpsys ===/, name: '转储系统服务', percent: 40 },
      { pattern: /=== dumpsys: done ===/, name: '系统服务转储完成', percent: 60 },
      { pattern: /=== dumpstate: done ===/, name: '设备状态收集完成', percent: 80 },
      { pattern: /== showmap ===/, name: '生成内存映射', percent: 85 },
      { pattern: /== System Trace ===/, name: '生成系统跟踪', percent: 90 },
      { pattern: /== Battery Service ===/, name: '电池服务', percent: 92 },
      { pattern: /== Phone State ===/, name: '电话状态', percent: 94 },
      { pattern: /== Network Policy ===/, name: '网络策略', percent: 96 },
      { pattern: /=== done ===/, name: '完成', percent: 100 },
    ];

    let matchedStages = new Set<number>();

    try {
      // 优先使用 shellProtocol
      const spawner = adb.subprocess.shellProtocol || adb.subprocess.noneProtocol;
      if (!spawner) {
        // 回退到 executeShell，使用更长的超时
        this.log('使用标准方式生成 Bugreport...', 'info');
        output = await this.executeShell(adb, 'bugreport', maxDuration);
        hasOutput = output.length > 0;
      } else {
        onProgress?.('正在启动 Bugreport...', 0);
        this.log('Bugreport 正在生成中，请稍候...', 'info');

        // 使用 spawn 而不是 spawnWaitText，避免超时
        const process = await spawner.spawn('bugreport');
        const stdout = (process as any).stdout || (process as any).output;

        if (!stdout) {
          throw new Error('无法获取输出流');
        }

        // 流式读取输出
        const reader = (stdout as ReadableStream<Uint8Array>).getReader();
        const decoder = new TextDecoder();
        let lastProgressTime = 0;
        let currentStatus = '正在生成 Bugreport...';

        while (true) {
          const { done, value } = await reader.read();

          if (done) break;

          const chunk = decoder.decode(value, { stream: true });
          output += chunk;
          hasOutput = true;

          // 检查进度阶段（每 500ms 检查一次，避免频繁回调）
          const now = Date.now();
          if (now - lastProgressTime > 500) {
            lastProgressTime = now;

            for (let i = 0; i < progressStages.length; i++) {
              if (!matchedStages.has(i) && progressStages[i].pattern.test(chunk)) {
                matchedStages.add(i);
                currentStatus = progressStages[i].name;
                onProgress?.(currentStatus, progressStages[i].percent);
                this.log(`Bugreport: ${currentStatus} (${progressStages[i].percent}%)`, 'info');
                break;
              }
            }

            // 如果没有匹配到新阶段但正在接收数据，更新为通用状态
            if (matchedStages.size === 0 && hasOutput) {
              const elapsed = Math.min(Math.floor((now - startTime) / 1000), 10);
              const percent = Math.min(5 + elapsed, 15); // 前10秒从5%渐变到15%
              onProgress?.(`正在收集数据 (${elapsed}s)...`, percent);
            }
          }

          // 每收到数据检查时长
          if (now - startTime > maxDuration) {
            reader.cancel();
            throw new Error('Bugreport 生成超时（超过10分钟），请检查设备是否需要切换到 USB 文件传输模式');
          }
        }

        reader.releaseLock();
      }

      if (!hasOutput || output.length === 0) {
        throw new Error('Bugreport 生成失败，未获取到输出。请尝试：\n1. 切换手机到"文件传输"或"MTP"模式\n2. 重新连接设备\n3. 检查手机屏幕是否弹出授权提示');
      }

      const duration = ((Date.now() - startTime) / 1000).toFixed(1);
      this.log(`Bugreport 生成完成，共 ${output.length} 字符，耗时 ${duration} 秒`, 'success');
      onProgress?.('完成', 100);

      return output;
    } catch (error: any) {
      if (error.name === 'TimeoutError' || error.message.includes('超时') || error.message.includes('timeout')) {
        this.log('Bugreport 生成超时。提示：\n1. 某些设备需要切换到"文件传输"模式\n2. 生成时间可能较长，请耐心等待', 'error');
      }
      throw error;
    }
  }

  /**
   * 获取 Wi-Fi 信息
   */
  async getWifiInfo(adb: Adb): Promise<{
    enabled: boolean;
    connected: boolean;
    ssid: string;
    bssid: string;
    frequency: string;
    wifiVersion: string;
    linkSpeed: string;
    txLinkSpeed: string;
    rxLinkSpeed: string;
    ipAddresses: string[];
    macAddress: string;
    signalStrength: number;
    password: string;
    availableNetworks: { ssid: string; bssid: string; level: number; frequency: string; capabilities: string }[];
  }> {
    this.log('正在获取 Wi-Fi 信息...', 'info');

    const result = {
      enabled: false,
      connected: false,
      ssid: '',
      bssid: '',
      frequency: '',
      wifiVersion: '',
      linkSpeed: '',
      txLinkSpeed: '',
      rxLinkSpeed: '',
      ipAddresses: [] as string[],
      macAddress: '',
      signalStrength: 0,
      password: '',
      availableNetworks: [] as { ssid: string; bssid: string; level: number; frequency: string; capabilities: string }[]
    };

    try {
      // 获取 Wi-Fi 状态
      const wifiState = await this.executeShell(adb, 'dumpsys wifi | grep "Wi-Fi is"');
      result.enabled = wifiState.includes('enabled');

      // 获取当前连接信息
      const wifiInfo = await this.executeShell(adb, 'dumpsys wifi | grep -A 30 "mWifiInfo"');

      const ssidMatch = wifiInfo.match(/SSID: ([^,\n]+)/);
      if (ssidMatch) result.ssid = ssidMatch[1].replace(/"/g, '').trim();

      const bssidMatch = wifiInfo.match(/BSSID: ([^,\n]+)/);
      if (bssidMatch) result.bssid = bssidMatch[1].trim();

      const freqMatch = wifiInfo.match(/Frequency: (\d+)/);
      if (freqMatch) {
        const freq = parseInt(freqMatch[1]);
        result.frequency = freq > 5000 ? '5 GHz' : '2.4 GHz';
        // 判断 Wi-Fi 版本
        if (freq > 5000) {
          result.wifiVersion = 'Wi-Fi 5 (802.11ac)';
        } else {
          result.wifiVersion = 'Wi-Fi 4 (802.11n)';
        }
      }

      const speedMatch = wifiInfo.match(/Link speed: (\d+)Mbps/);
      if (speedMatch) result.linkSpeed = `${speedMatch[1]} Mbps`;

      const txSpeedMatch = wifiInfo.match(/Tx Link speed: (\d+)Mbps/);
      if (txSpeedMatch) result.txLinkSpeed = `${txSpeedMatch[1]} Mbps`;

      const rxSpeedMatch = wifiInfo.match(/Rx Link speed: (\d+)Mbps/);
      if (rxSpeedMatch) result.rxLinkSpeed = `${rxSpeedMatch[1]} Mbps`;

      const rssiMatch = wifiInfo.match(/RSSI: (-?\d+)/);
      if (rssiMatch) result.signalStrength = parseInt(rssiMatch[1]);

      result.connected = result.ssid !== '' && result.ssid !== '<unknown ssid>';

      // 获取所有 IP 地址
      const ipInfo = await this.executeShell(adb, 'ip addr show wlan0 2>/dev/null');
      const ipv4Matches = ipInfo.matchAll(/inet (\d+\.\d+\.\d+\.\d+)/g);
      const ipv6Matches = ipInfo.matchAll(/inet6 ([a-f0-9:]+)/g);
      for (const m of ipv4Matches) result.ipAddresses.push(m[1]);
      for (const m of ipv6Matches) {
        if (!m[1].startsWith('fe80')) result.ipAddresses.push(m[1]); // 排除链路本地地址
      }

      // 获取 MAC 地址
      const macInfo = await this.executeShell(adb, 'cat /sys/class/net/wlan0/address 2>/dev/null');
      if (macInfo) result.macAddress = macInfo.trim();

      // 获取可用网络列表
      const scanResults = await this.executeShell(adb, 'dumpsys wifi | grep -A 200 "Latest scan results"');
      const lines = scanResults.split('\n');

      for (const line of lines) {
        if (line.includes('SSID:') && line.includes('BSSID:')) {
          const netSsid = line.match(/SSID: ([^,]+)/)?.[1]?.replace(/"/g, '').trim();
          const netBssid = line.match(/BSSID: ([^,]+)/)?.[1]?.trim();
          const netLevel = parseInt(line.match(/level: (-?\d+)/)?.[1] || '0');
          const netFreq = line.match(/frequency: (\d+)/)?.[1];
          const caps = line.match(/capabilities: \[([^\]]+)\]/)?.[1] || '';

          if (netSsid && !result.availableNetworks.find(n => n.ssid === netSsid)) {
            result.availableNetworks.push({
              ssid: netSsid,
              bssid: netBssid || '',
              level: netLevel,
              frequency: netFreq ? (parseInt(netFreq) > 5000 ? '5G' : '2.4G') : '',
              capabilities: caps.includes('WPA') ? 'WPA' : (caps.includes('WEP') ? 'WEP' : '')
            });
          }
        }
      }

      this.log('Wi-Fi 信息获取完成', 'success');
    } catch (e: any) {
      this.log(`获取 Wi-Fi 信息失败: ${e.message}`, 'warning');
    }

    return result;
  }

  async uploadFile(adb: Adb, destPath: string, file: File): Promise<void> {
    this.log(`正在上传文件: ${file.name} -> ${destPath}`, 'info');
    const sync = await adb.sync();
    try {
      const fullPath = destPath + (destPath.endsWith('/') ? '' : '/') + file.name;

      await sync.write({
        filename: fullPath,
        file: file.stream() as any,
        mtime: Math.floor(file.lastModified / 1000),
        permission: 0o666,
      });

      this.log(`文件上传成功: ${file.name}`, 'success');
    } catch (error: any) {
      this.log(`文件上传失败: ${file.name}, 错误: ${error.message}`, 'error');
      throw error;
    } finally {
      sync.dispose();
    }
  }

  async uploadDirectory(adb: Adb, destPath: string, dirHandle: FileSystemDirectoryHandle): Promise<void> {
    const dirName = dirHandle.name;
    const currentDestPath = destPath + (destPath.endsWith('/') ? '' : '/') + dirName;

    this.log(`正在同步目录: ${dirName} -> ${currentDestPath}`, 'info');
    await this.createDirectory(adb, currentDestPath);

    // 收集所有子项
    const entries: { kind: string; handle: FileSystemHandle }[] = [];
    for await (const entry of (dirHandle as any).values()) {
      entries.push({ kind: entry.kind, handle: entry });
    }

    // 顺序处理（ADB sync 不支持并行）
    for (const entry of entries) {
      if (entry.kind === 'file') {
        const file = await (entry.handle as FileSystemFileHandle).getFile();
        await this.uploadFile(adb, currentDestPath, file);
      } else {
        await this.uploadDirectory(adb, currentDestPath, entry.handle as FileSystemDirectoryHandle);
      }
    }
  }

  async createDirectory(adb: Adb, path: string): Promise<void> {
    this.log(`正在创建目录: ${path}`, 'info');
    await this.executeShell(adb, `mkdir -p "${path}"`);
  }

  async deletePath(adb: Adb, path: string): Promise<void> {
    this.log(`正在删除: ${path}`, 'info');
    await this.executeShell(adb, `rm -rf "${path}"`);
  }

  /**
   * 提取华为 HiTrack 运动数据
   */
  async extractHiTrack(
    adb: Adb,
    targetHandle: FileSystemDirectoryHandle,
    startDate?: Date,
    endDate?: Date,
    onProgress?: (current: number, total: number) => void
  ): Promise<{ count: number; totalBytes: number }> {
    const basePath = '/data/data/com.huawei.health/files';

    // 搜索 HiTrack 文件
    this.log('正在搜索 HiTrack 文件...', 'info');
    const output = await this.executeShell(adb, `find ${basePath} -maxdepth 1 -type f -name '*HiTrack*' 2>/dev/null`);

    if (!output.trim()) {
      throw new Error('未找到 HiTrack 文件，请确认已安装华为运动健康应用');
    }

    let files = output.trim().split('\n').filter(Boolean);

    // 按日期筛选
    if (startDate || endDate) {
      files = files.filter(filePath => {
        const name = filePath.split('/').pop() || '';
        const tsMatch = name.match(/HiTrack_(\d{10})/);
        if (!tsMatch) return false;
        const fileDate = new Date(parseInt(tsMatch[1]) * 1000);
        if (startDate && fileDate < startDate) return false;
        if (endDate && fileDate > endDate) return false;
        return true;
      });
    }

    if (files.length === 0) {
      throw new Error('指定日期范围内没有 HiTrack 文件');
    }

    this.log(`找到 ${files.length} 个 HiTrack 文件`, 'success');

    let totalBytes = 0;
    let downloaded = 0;
    const usedNames = new Set<string>();

    // 预处理文件名映射
    const fileMap: { path: string; originalName: string; newName: string }[] = [];
    for (const filePath of files) {
      const originalName = filePath.split('/').pop() || '';
      let newName = originalName;
      const tsMatch = originalName.match(/^(HiTrack_)(\d{10})/);
      if (tsMatch) {
        const date = new Date(parseInt(tsMatch[2]) * 1000);
        const y = date.getFullYear();
        const m = String(date.getMonth() + 1).padStart(2, '0');
        const d = String(date.getDate()).padStart(2, '0');
        const h = String(date.getHours()).padStart(2, '0');
        const min = String(date.getMinutes()).padStart(2, '0');
        const s = String(date.getSeconds()).padStart(2, '0');
        newName = `${tsMatch[1]}${y}-${m}-${d}_${h}-${min}-${s}`;
      }
      if (!usedNames.has(newName)) {
        usedNames.add(newName);
        fileMap.push({ path: filePath, originalName, newName });
      }
    }

    // 动态并发下载
    let concurrency = Math.min(navigator.hardwareConcurrency || 4, 8);
    const queue = [...fileMap];
    const active = new Set<Promise<void>>();
    let completed = 0;
    let recentFails = 0;

    const processFile = async (file: { path: string; originalName: string; newName: string }): Promise<boolean> => {
      const sync = await adb.sync();
      try {
        const fileHandle = await targetHandle.getFileHandle(file.newName, { create: true });
        const writable = await fileHandle.createWritable();
        const stream = await sync.read(file.path);
        await (stream as ReadableStream<Uint8Array>).pipeTo(writable);

        const localFile = await fileHandle.getFile();
        totalBytes += localFile.size;
        downloaded++;
        return true;
      } finally {
        sync.dispose();
      }
    };

    while (queue.length > 0 || active.size > 0) {
      while (active.size < concurrency && queue.length > 0) {
        const file = queue.shift()!;
        const promise = processFile(file).then(() => {
          this.log(`已下载: ${file.originalName} -> ${file.newName}`, 'info');
          recentFails = 0;
          if (concurrency < 12) concurrency++;
        }).catch(e => {
          this.log(`下载失败 ${file.newName}: ${e.message}`, 'error');
          recentFails++;
          if (recentFails > 2 && concurrency > 2) concurrency--;
        }).finally(() => {
          completed++;
          onProgress?.(completed, fileMap.length);
          active.delete(promise);
        });
        active.add(promise);
      }
      if (active.size > 0) {
        await Promise.race(active);
      }
    }

    return { count: downloaded, totalBytes };
  }
}

export const webAdbService = WebAdbService.getInstance();
